import React, { useState } from "react";
import {
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Download
} from "lucide-react";
import MasterLayout from "../masterLayout/MasterLayout";
import { Breadcrumb } from "react-bootstrap";
import config from "../config";

export default function Validator() {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(selected || null);
    setMessage("");
    setDownloadUrl(null);
  };

const handleValidate = async () => {
  if (!file) return setMessage("Please upload an Excel file first.");

  setStatus("validating");
  setMessage("");

  const form = new FormData();
  form.append("file", file);

  try {
    const response = await fetch(`${config.apiUrlBetaV2}validateExcel`, {
      method: "POST",
      body: form, // DO NOT add Content-Type manually
    });
    console.log(response);

    if (!response.ok) throw new Error("Failed");

    // Lambda returns base64 string
    const base64Excel = await response.text(); 

    // Convert base64 to binary
    const binary = atob(base64Excel);
    const len = binary.length;
    const buffer = new ArrayBuffer(len);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < len; i++) {
      view[i] = binary.charCodeAt(i);
    }

    const blob = new Blob([view], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "validated.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);

    setStatus("paid");
    setMessage("Excel validated and downloaded successfully!");
  } catch (err) {
    console.error(err);
    setStatus("failed");
    setMessage("Validation failed.");
  }
};


  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = "validated.xlsx";
    link.click();

    URL.revokeObjectURL(downloadUrl);
    setDownloadUrl(null);
  };

  const reset = () => {
    setFile(null);
    setStatus("idle");
    setMessage("");
    setDownloadUrl(null);
  };

  const statusClasses = {
    idle: "px-3 py-2 rounded-pill bg-secondary text-white fw-semibold",
    validating: "px-3 py-2 rounded-pill bg-warning text-dark fw-semibold",
    paid: "px-3 py-2 rounded-pill bg-success text-white fw-semibold",
    failed: "px-3 py-2 rounded-pill bg-danger text-white fw-semibold",
  }[status];

  return (
    <MasterLayout>
      <Breadcrumb title="Validate-validate" />
      <div
        className="min-vh-100 w-100 d-flex flex-column align-items-center justify-content-start p-5"
        style={{ background: "linear-gradient(135deg, #eef4ff, #e6e9ff)" }}
      >
        <div style={{ width: "100%", maxWidth: "850px" }}>

          {/* HEADER BOX */}
          <div className="p-3 bg-white rounded-3 shadow-sm mb-3 border">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="h5 fw-bold text-dark mb-1">Excel Payment Validator</h2>
                <small className="text-muted">Upload excel & verify payment status.</small>
              </div>

              <button
                onClick={handleValidate}
                className="btn btn-success btn-sm px-3 d-flex align-items-center"
              >
                <i className="bi bi-check-circle me-1"></i> Validate
              </button>
            </div>
          </div>

          {/* MAIN UPLOAD CARD */}
          <div className="bg-white shadow-lg rounded-4 p-5 border border-light">

            <label
              className="w-100 p-5 border border-2 border-secondary rounded-4 text-center bg-light"
              style={{ cursor: "pointer", borderStyle: "dashed" }}
            >
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="d-none"
              />
              <Upload size={50} className="text-secondary mb-3" />
              <p className="fs-4 fw-semibold text-dark">Click to Upload Excel</p>
              <p className="text-muted small">Only .xlsx and .xls files supported</p>
            </label>

            {/* FILE PREVIEW */}
            {file && (
              <div className="mt-4 d-flex align-items-center gap-3 bg-light p-3 rounded-3 border">
                <FileSpreadsheet size={30} className="text-primary" />
                <div>
                  <div className="fw-bold text-dark">{file.name}</div>
                  <div className="small text-muted">Ready for validation</div>
                </div>
              </div>
            )}

            {/* STATUS */}
            <div className="mt-4">
              <span className={statusClasses}>
                {status === "validating" && (
                  <Loader2
                    size={16}
                    className="me-2"
                    style={{ animation: "spin 1s linear infinite" }}
                  />
                )}
                {status === "paid" && <CheckCircle2 size={16} className="me-2" />}
                {status}
              </span>
            </div>

            {message && <div className="alert alert-info mt-4">{message}</div>}

            {/* BUTTONS */}
            <div className="mt-4 d-flex gap-3">
              <button
                onClick={handleValidate}
                disabled={status === "validating"}
                className="btn btn-primary flex-fill py-2 fw-semibold rounded-3 shadow-sm"
              >
                {status === "validating" ? "Validating..." : "Validate Payment"}
              </button>

              <button
                onClick={reset}
                className="btn btn-outline-secondary d-flex align-items-center gap-2 px-4 rounded-3"
              >
                <RefreshCcw size={16} /> Reset
              </button>
            </div>

            {/* DOWNLOAD BUTTON */}
            {downloadUrl && (
              <button
                onClick={handleDownload}
                className="btn btn-success w-100 mt-4 d-flex align-items-center justify-content-center gap-2"
              >
                <Download size={18} /> Download Validated Excel
              </button>
            )}
          </div>

          <p className="text-muted small mt-4 text-center">
            StuCred Internal Payment Validation Tool
          </p>
        </div>
      </div>
    </MasterLayout>
  );
}
